/**
 * Image skeleton
 */

import onFragment, { within } from './on-fragment'

/*
 * Clears the `behind` skeleton (docs/features/PLACEHOLDERS.md §5, §16) once
 * its image is in. The skeleton sits behind the image and was meant to be
 * covered by it - which a photo does and a transparent svg or png logo does
 * not: the grey box showed through permanently, most visibly in the logo
 * marquee. So the skeleton goes on the image's `load`, and on `error` too,
 * since a broken image will never cover it either.
 *
 * One capture-phase listener on the document rather than one per image:
 * `load` does not bubble but it does capture, so this also covers images that
 * arrive later - the listing's "load more", slides swiper builds, lazy images
 * scrolled into view. Images that finished before this script ran are swept
 * below, for the page and for every fragment htmx inserts.
 *
 * The macro renders its spans as direct children of the image's wrapper,
 * the image either beside them or inside a link, so the lookup walks up a
 * few levels to the first element holding a skeleton. The map clears its own
 * (interactive-map.js), and Leaflet's tile images must not do it early.
 */
const SKELETON_DEPTH = 3
const SKELETON = ':scope > .placeholder[aria-hidden="true"]'

const skeletonHost = (img) => {
  let el = img.parentElement
  for (let depth = 0; el && depth < SKELETON_DEPTH; depth++) {
    if (el.classList.contains('interactive-map')) return null
    const placeholder = el.querySelector(SKELETON)
    if (placeholder) return el
    el = el.parentElement
  }
  return null
}

const clearImageSkeleton = (img) => {
  const host = skeletonHost(img)
  if (!host) return
  const placeholder = host.querySelector(SKELETON)
  const loading = placeholder.nextElementSibling
  if (loading && loading.classList.contains('visually-hidden')) loading.remove()
  placeholder.remove()
  host.classList.remove('placeholder-glow')
}

const onImageSettled = (event) => {
  if (event.target instanceof HTMLImageElement) clearImageSkeleton(event.target)
}

document.addEventListener('load', onImageSettled, true)
document.addEventListener('error', onImageSettled, true)

// The sweep for images that are in already: the page now, and every fragment
// htmx swaps in (an image from the cache can be complete before its load
// listener above sees it)
onFragment((root) => {
  within(root, 'img').forEach((img) => {
    if (img.complete) clearImageSkeleton(img)
  })
})
